export const formatAgreementBlockLabel = (block) => {
  const name = block?.block?.name || block?.name || "Блок";
  const floors = Number(block?.floor_count);
  if (Number.isFinite(floors) && floors > 0) {
    return `${name} (${floors} эт.)`;
  }
  return name;
};

export const getAgreementBlockPlanCost = (block) =>
  (Number(block?.money_expenditures) || 0) +
  (Number(block?.bonus_money_expenditures) || 0);

/** Плановая стоимость раздела — та же логика, что в смете (WorkTab). */
export const getAgreementSectionPlanCost = (section, blocks = []) => {
  if (section?.amount != null) return Number(section.amount) || 0;
  if (blocks.length > 0) {
    return blocks.reduce(
      (sum, block) => sum + getAgreementBlockPlanCost(block),
      0,
    );
  }
  return Number(section?.money_expenditures?.planned) || 0;
};

/** Плановая стоимость колонки вкладок BIM / качество / ведение / управление. */
export const getModuleRolePlanCost = (role) =>
  (Number(role?.money_expenditures?.direct) || 0) +
  (Number(role?.money_expenditures?.bonus) || 0);

export const AGREEMENT_MODULE_SOURCES = [
  {
    key: "bim_coordination",
    groupKey: "bim",
    title: "BIM координирование",
  },
  {
    key: "quality_control",
    groupKey: "quality",
    title: "Контроль качества",
  },
  {
    key: "project_delivery",
    groupKey: "lead",
    title: "Ведение проекта",
  },
  {
    key: "project_management",
    groupKey: "project",
    title: "Управление проектом",
  },
];

const SECTION_MODULES_PREFIX = "sections:";

/** Строка для payment_schedule.template: какие вкладки выбраны у разделов. */
export const buildSectionModulesTemplate = (entries) => {
  const parts = [];
  const seen = new Set();
  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const sectionId = Number(entry?.sectionId);
    const moduleKey = entry?.moduleKey;
    if (!Number.isFinite(sectionId) || !moduleKey) return;
    const token = `${sectionId}=${moduleKey}`;
    if (seen.has(token)) return;
    seen.add(token);
    parts.push(token);
  });
  if (parts.length === 0) return null;
  return `${SECTION_MODULES_PREFIX}${parts.join(",")}`;
};

/** sectionId -> ключи вкладок сметы, сохранённые в соглашении. */
export const parseSectionModulesTemplate = (template) => {
  const map = {};
  if (
    typeof template !== "string" ||
    !template.startsWith(SECTION_MODULES_PREFIX)
  ) {
    return map;
  }
  template
    .slice(SECTION_MODULES_PREFIX.length)
    .split(",")
    .forEach((part) => {
      const [rawId, moduleKey] = part.split("=");
      const sectionId = Number(rawId);
      if (!Number.isFinite(sectionId) || !moduleKey) return;
      if (!map[sectionId]) map[sectionId] = [];
      if (!map[sectionId].includes(moduleKey)) map[sectionId].push(moduleKey);
    });
  return map;
};

export const getAgreementSectionModuleTitles = (agreement, sectionId) => {
  const keys =
    parseSectionModulesTemplate(agreement?.payment_schedule?.template)[
      Number(sectionId)
    ] || [];
  return keys
    .map(
      (key) => AGREEMENT_MODULE_SOURCES.find((item) => item.key === key)?.title,
    )
    .filter(Boolean);
};

const moduleSelectionKey = (moduleKey, index) => {
  const source = AGREEMENT_MODULE_SOURCES.find((item) => item.key === moduleKey);
  if (!source) return null;
  return `${source.groupKey}:${index}`;
};

/** Подменяет разделы рабочего проекта на роли той вкладки, с которой их сохранили. */
export const applyStoredModuleSelections = (agreement, estimateData, state) => {
  const modulesBySection = parseSectionModulesTemplate(
    agreement?.payment_schedule?.template,
  );
  const sectionIds = Object.keys(modulesBySection);
  if (!estimateData || sectionIds.length === 0) return state;

  const selectedSections = new Set(state.selectedSections);
  const agreementCosts = { ...(state.agreementCosts || {}) };
  const selectedBlocks = { ...(state.selectedBlocks || {}) };
  const priceBySection = new Map();
  (Array.isArray(agreement?.section_prices) ? agreement.section_prices : []).forEach(
    (item) => {
      const sectionId = Number(item?.section_id);
      if (!Number.isFinite(sectionId)) return;
      priceBySection.set(sectionId, Number(item?.price) || 0);
    },
  );

  sectionIds.forEach((rawSectionId) => {
    const sectionId = Number(rawSectionId);
    const moduleKeys = modulesBySection[sectionId] || [];
    selectedSections.delete(sectionId);
    delete selectedBlocks[sectionId];
    const carriedCost = agreementCosts[sectionId];
    delete agreementCosts[sectionId];

    moduleKeys.forEach((moduleKey) => {
      const roles = Array.isArray(estimateData?.[moduleKey]?.roles)
        ? estimateData[moduleKey].roles
        : [];
      const match = roles
        .map((role, index) => ({ role, index }))
        .find(
          ({ role }) =>
            role?.section_id != null &&
            Number(role.section_id) === sectionId,
        );
      const selectionKey = match
        ? moduleSelectionKey(moduleKey, match.index)
        : null;
      if (!selectionKey) return;
      selectedSections.add(selectionKey);
      if (carriedCost) {
        agreementCosts[selectionKey] = carriedCost;
        return;
      }
      if (priceBySection.has(sectionId)) {
        agreementCosts[selectionKey] = {
          section_total: priceBySection.get(sectionId),
        };
      }
    });
  });

  return { selectedSections, selectedBlocks, agreementCosts };
};

const ADVANCE_WORKING_DAYS_TERMS =
  "Оплата в течение 10 (десяти) рабочих дней после подписания Сторонами Договора";
const BANKING_DAYS_TERMS = "Оплата в течение 10 (десяти) банковских дней";

export const PAYMENT_TEMPLATE_PERCENTAGES = {
  "30_50_20": [30, 50, 20],
  "15_15_32_38": [15, 15, 32, 38],
  "30_40_30": [30, 40, 30],
  "50_50": [50, 0, 50],
  "70_30": [70, 0, 30],
  "80_20": [80, 0, 20],
  "100": [100, 0, 0],
};

/** Условия этапов для шаблонов, которые не совпадают с базовыми ОДГЭ/ОПГЭ. */
export const PAYMENT_TEMPLATE_STAGES = {
  "15_15_32_38": [
    {
      label: "Аванс",
      description: "После подписания Сторонами Договора",
      terms: ADVANCE_WORKING_DAYS_TERMS,
    },
    {
      label: "Первый этап/пакет",
      description: "После согласования первого этапа/пакета",
      terms: BANKING_DAYS_TERMS,
    },
    {
      label: "Второй этап/пакет",
      description:
        "После согласования второго этапа/пакета (перед заходом в экспертизу)",
      terms: BANKING_DAYS_TERMS,
    },
    {
      label: "Третий этап/пакет",
      description:
        "После согласования третьего этапа/пакета (ПСД после экспертизы)",
      terms: BANKING_DAYS_TERMS,
    },
  ],
  "30_40_30": [
    {
      label: "Аванс",
      description: "После подписания Сторонами Договора",
      terms: ADVANCE_WORKING_DAYS_TERMS,
    },
    {
      label: "Прием работ",
      description:
        "После приема работ и заключения договора с РГП «Госэкспертизы»",
      terms: BANKING_DAYS_TERMS,
    },
    {
      label: "Приемка проекта",
      description:
        "После получения заключения экспертизы и принятия заказчиком проекта с учетом отработки всех замечаний (коллизии) со стороны заказчика",
      terms: BANKING_DAYS_TERMS,
    },
  ],
};

/** Текст этапа для карточки соглашения: условие оплаты, иначе короткое название. */
export const getPaymentTemplateStageLabel = (templateKey, index) => {
  const stage = PAYMENT_TEMPLATE_STAGES[templateKey]?.[index];
  if (!stage) return null;
  return stage.description || stage.label || null;
};

export const BASE_PAYMENT_STAGES_COUNT = 3;
export const MAX_PAYMENT_STAGES_COUNT = 10;

/**
 * Нормализует проценты в массив [аванс, ОДГЭ, ОПГЭ, ...доп. этапы].
 * Минимум 3 элемента, дополнительные этапы сохраняются.
 */
export const normalizePaymentPercentages = (percentages) => {
  const source = Array.isArray(percentages) ? percentages : [];
  const length = Math.min(
    MAX_PAYMENT_STAGES_COUNT,
    Math.max(BASE_PAYMENT_STAGES_COUNT, source.length),
  );
  return Array.from({ length }, (_, index) => {
    const value = Number(source[index]);
    return Number.isFinite(value) ? value : 0;
  });
};

/** Убирает нулевые дополнительные этапы в конце (базовые 3 позиции сохраняются). */
export const trimPaymentPercentages = (percentages) => {
  const normalized = normalizePaymentPercentages(percentages);
  let length = normalized.length;
  while (length > BASE_PAYMENT_STAGES_COUNT && normalized[length - 1] === 0) {
    length -= 1;
  }
  return normalized.slice(0, length);
};

const isApiDateOnlyString = (value) =>
  typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);

/** Парсит YYYY-MM-DD как локальную календарную дату (без сдвига UTC). */
export const parseApiDateValue = (value) => {
  if (!value) return null;
  if (isApiDateOnlyString(value)) {
    const [year, month, day] = value.split("-").map(Number);
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

/** Форматирует Date в YYYY-MM-DD по локальному календарю (без toISOString). */
export const formatDateForApi = (date = new Date()) => {
  const d =
    date instanceof Date ? date : parseApiDateValue(date) ?? new Date(date);
  if (!d || Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const resolvePaymentTemplateFromPercentages = (percentages) => {
  if (!Array.isArray(percentages)) return "30_50_20";
  const normalized = trimPaymentPercentages(percentages);
  const match = Object.entries(PAYMENT_TEMPLATE_PERCENTAGES).find(
    ([, template]) =>
      template.length === normalized.length &&
      template.every((value, index) => value === normalized[index]),
  );
  return match?.[0] ?? "custom";
};

/** Категория контрагента для радиокнопок типа исполнителя. */
export const getCounterpartyLegalCategory = (item) => {
  if (!item) return null;
  const legalForm = String(item.legal_form || "").trim().toLowerCase();
  const entityType = String(
    item.type || item.counterparty_type || "",
  )
    .trim()
    .toLowerCase();
  const isNdsPayer = Boolean(item.is_nds_payer);

  if (legalForm.includes("гпх") || legalForm.includes("физическое лицо")) {
    return "individual_gph";
  }
  if (legalForm === "ип" || legalForm.startsWith("ип ")) {
    return "ip";
  }
  if (legalForm.includes("тоо") || legalForm.includes("llp")) {
    return isNdsPayer ? "too_with_vat" : "too_without_vat";
  }
  if (entityType === "physical_entity") {
    return "ip";
  }
  return isNdsPayer ? "too_with_vat" : "too_without_vat";
};

export const matchesPerformerType = (item, performerType) =>
  getCounterpartyLegalCategory(item) === performerType;

export const mapCounterpartyToUiPerformerType = (counterparty) => {
  if (!counterparty) return "too_with_vat";
  return getCounterpartyLegalCategory(counterparty) || "too_with_vat";
};

export const resolveUiPerformerTypeFromAgreement = async (
  agreement,
  getAllCounterparties,
) => {
  const apiType = agreement?.performer_type;
  if (apiType === "contract_employee") return "contract_employee";
  if (apiType === "gph_physical") return "individual_gph";
  if (agreement?.counterparty_id == null) return "too_with_vat";

  try {
    const list = await getAllCounterparties({
      counterparty_side: "counterparty",
    });
    const counterparty = (Array.isArray(list) ? list : []).find(
      (item) => Number(item.id) === Number(agreement.counterparty_id),
    );
    return mapCounterpartyToUiPerformerType(counterparty);
  } catch {
    return "too_with_vat";
  }
};

export const buildAgreementBlocksState = (agreement, sectionsData) => {
  const sectionIds = Array.isArray(agreement?.section_ids)
    ? agreement.section_ids.map(Number).filter((id) => Number.isFinite(id))
    : [];

  const blockPrices = new Map();
  [
    ...(agreement?.agreement_blocks_detail || []),
    ...(agreement?.agreement_blocks || []),
  ].forEach((item) => {
    if (item?.block_id != null) {
      blockPrices.set(Number(item.block_id), Number(item.price) || 0);
    }
  });

  const sectionPriceById = new Map();
  (Array.isArray(agreement?.section_prices) ? agreement.section_prices : []).forEach(
    (item) => {
      const pricedSectionId = Number(item?.section_id);
      if (!Number.isFinite(pricedSectionId)) return;
      sectionPriceById.set(pricedSectionId, Number(item?.price) || 0);
    },
  );

  const selectedSections = new Set(sectionIds);
  const selectedBlocks = {};
  const agreementCosts = {};

  sectionIds.forEach((sectionId) => {
    const section = sectionsData[sectionId];
    const blocks = section?.block_expenditures || [];

    if (blocks.length === 0) {
      if (sectionPriceById.has(sectionId)) {
        agreementCosts[sectionId] = {
          section_total: sectionPriceById.get(sectionId),
        };
      } else if (sectionIds.length === 1 && sectionPriceById.size === 0) {
        agreementCosts[sectionId] = {
          section_total: Number(agreement?.price) || 0,
        };
      }
      return;
    }

    selectedBlocks[sectionId] = {};
    agreementCosts[sectionId] = {};

    blocks.forEach((block, blockIndex) => {
      const blockId = Number(block?.id ?? block?.block?.id);
      const price = blockPrices.get(blockId);
      if (price != null && price > 0) {
        selectedBlocks[sectionId][blockIndex] = true;
        agreementCosts[sectionId][blockIndex] = price;
      }
    });
  });

  return { selectedSections, selectedBlocks, agreementCosts };
};

export const buildAgreementBlocksPayload = (
  selectedSections,
  sectionsData,
  agreementCosts,
) => {
  const blocks = [];
  const blockIds = [];
  const sectionPrices = [];

  Array.from(selectedSections).forEach((sectionId) => {
    const section = sectionsData[sectionId];
    const blockExpenditures = section?.block_expenditures || [];
    const sectionCosts = agreementCosts[sectionId] || {};
    const numericSectionId = Number(sectionId);

    if (blockExpenditures.length === 0) {
      const price = Number(sectionCosts.section_total);
      if (!Number.isFinite(numericSectionId) || !price) return;
      sectionPrices.push({
        section_id: numericSectionId,
        price: Math.round(price),
      });
      return;
    }

    Object.keys(sectionCosts).forEach((blockIndexStr) => {
      if (blockIndexStr === "section_total") return;
      const blockIndex = Number(blockIndexStr);
      if (!Number.isFinite(blockIndex)) return;
      const cost = Number(sectionCosts[blockIndexStr]);
      if (!cost) return;
      const block = blockExpenditures[blockIndex];
      const blockId = block?.id ?? block?.block?.id;
      if (blockId == null) return;
      blockIds.push(Number(blockId));
      blocks.push({ block_id: Number(blockId), price: Math.round(cost) });
    });
  });

  return { block_ids: blockIds, blocks, section_prices: sectionPrices };
};
